import React from "react";
import { Row, Col, Card, CardBody } from "reactstrap";

export const formatCurrency = (value) => {
  const amount = Number(value) || 0;
  return `RM ${amount.toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const StatCard = ({ icon, label, value, subValue, color = "#00BFA6" }) => (
  <div className="col-md-3 col-sm-6 mb-3">
    <div className="card h-100 border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <p className="text-muted small mb-1 fw-bold">{label}</p>
            <h4 className="mb-0 fw-bold text-dark">{value}</h4>
            {subValue && <p className="text-muted small mb-0 mt-1">{subValue}</p>}
          </div>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 42, height: 42, backgroundColor: `${color}20`, color }}
          >
            <i className={`${icon} fs-5`}></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const DetailRow = ({ label, value, highlight }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted small">{label}</span>
    <span className={`fw-bold ${highlight || "text-dark"}`}>{value}</span>
  </div>
);

export const StatsPanel = ({ loading, stats, t, children }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">{t("Loading...")}</span>
        </div>
        <p className="text-muted mt-2 mb-0">{t("Loading doctor statistics...")}</p>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="alert alert-light border mb-0">
        {t("Unable to load doctor statistics.")}
      </div>
    );
  }
  return children;
};

export const RecentTransactionsList = ({ transactions, t }) => {
  if (!transactions?.length) {
    return <p className="text-muted fst-italic mb-0">{t("No transactions found")}</p>;
  }

  return transactions.map((txn) => (
    <div
      key={txn._id}
      className="p-2 mb-2 rounded bg-light border-start border-3"
      style={{
        borderColor:
          txn.ladger_action === "+"
            ? "#4CAF50"
            : txn.ladger_action === "-"
            ? "#f44336"
            : "#FF9800",
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="me-2">
          <div className="fw-bold small text-dark">
            {txn.required_action || t("Transaction")}
          </div>
          <div className="text-muted small">
            {formatDate(txn.datetime_added)}
            {txn.transaction_number ? ` · #${txn.transaction_number}` : ""}
          </div>
        </div>
        <div className="text-end">
          <div
            className={`fw-bold small ${
              txn.ladger_action === "+"
                ? "text-success"
                : txn.ladger_action === "-"
                ? "text-danger"
                : "text-dark"
            }`}
          >
            {txn.ladger_action === "+" ? "+" : txn.ladger_action === "-" ? "-" : ""}
            {formatCurrency(txn.doctor_actual_amount || txn.total_amount)}
          </div>
          <span
            className={`badge ${
              txn.status === "Paid"
                ? "bg-success"
                : txn.status === "Pending" || txn.status === "In Progress"
                ? "bg-warning text-dark"
                : "bg-secondary"
            }`}
            style={{ fontSize: "0.65rem" }}
          >
            {txn.status || "-"}
          </span>
        </div>
      </div>
    </div>
  ));
};

const DoctorStatsPanel = ({ section, stats, loading, t, onRefresh }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">{t("Loading...")}</span>
        </div>
        <p className="text-muted mt-2 mb-0">{t("Loading doctor statistics...")}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <>
        {section === "overview" && onRefresh && (
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onRefresh}
            >
              <i className="pe-7s-refresh"></i> {t("Refresh")}
            </button>
          </div>
        )}
        <div className="alert alert-light border mb-0">
          {t("Unable to load doctor statistics.")}
        </div>
      </>
    );
  }

  if (section === "overview") {
    return (
      <>
        <div className="d-flex justify-content-end mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <i className="pe-7s-refresh"></i> {t("Refresh")}
          </button>
        </div>
        <StatsPanel loading={false} stats={stats} t={t}>
          <Row>
            <StatCard
              icon="pe-7s-users"
              label={t("Total Patients")}
              value={stats.patients?.unique_patients || 0}
              color="#2196F3"
            />
            <StatCard
              icon="pe-7s-date"
              label={t("Completed Appointments")}
              value={stats.appointments?.completed || 0}
              subValue={`${t("Cancelled")}: ${stats.appointments?.cancelled || 0}`}
              color="#4CAF50"
            />
            <StatCard
              icon="pe-7s-cash"
              label={t("Total Earned")}
              value={formatCurrency(stats.revenue?.ledger_total)}
              subValue={`${t("In Progress")}: ${formatCurrency(stats.revenue?.in_progress_amount)}`}
              color="#FF9800"
            />
            <StatCard
              icon="pe-7s-wallet"
              label={t("Total Withdrawn")}
              value={formatCurrency(stats.withdrawals?.total_withdrawn)}
              subValue={`${t("Pending")}: ${stats.withdrawals?.pending || 0}`}
              color="#9C27B0"
            />
          </Row>
          <Row className="mt-2">
            <Col md="4" className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <h6 className="fw-bold mb-3">
                    <i className="pe-7s-id me-2 text-primary"></i>
                    {t("Account Summary")}
                  </h6>
                  <DetailRow label={t("Specialization")} value={stats.account?.specialization || "-"} />
                  <DetailRow label={t("Consultancy Rate")} value={stats.account?.rate ? formatCurrency(stats.account.rate) : "-"} />
                  <DetailRow label={t("Experience")} value={stats.account?.experience ? `${stats.account.experience} ${t("years")}` : "-"} />
                  <DetailRow label={t("Clinic")} value={stats.account?.clinic_name || "-"} />
                  <DetailRow label={t("Member Since")} value={formatDate(stats.account?.member_since)} />
                  <DetailRow label={t("Status")} value={stats.account?.status || "-"} />
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <h6 className="fw-bold mb-3">
                    <i className="pe-7s-star me-2 text-warning"></i>
                    {t("Reviews")}
                  </h6>
                  <DetailRow label={t("Total Reviews")} value={stats.reviews?.total || 0} />
                  <DetailRow
                    label={t("Average Rating")}
                    value={
                      stats.reviews?.total > 0
                        ? `${stats.reviews.average_rating} / 5`
                        : t("No reviews yet")
                    }
                    highlight="text-warning"
                  />
                </CardBody>
              </Card>
            </Col>
            <Col md="4" className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <h6 className="fw-bold mb-3">
                    <i className="pe-7s-graph2 me-2 text-success"></i>
                    {t("Quick Stats")}
                  </h6>
                  <DetailRow label={t("Pending Appointments")} value={stats.appointments?.pending || 0} highlight="text-warning" />
                  <DetailRow label={t("Total Bids Submitted")} value={stats.appointments?.total_bids || 0} />
                  <DetailRow label={t("Ledger Transactions")} value={stats.ledger?.total_transactions || 0} />
                  <DetailRow label={t("Withdrawal Requests")} value={stats.withdrawals?.total || 0} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </StatsPanel>
      </>
    );
  }

  if (section === "appointments") {
    return (
      <StatsPanel loading={false} stats={stats} t={t}>
        <Row>
          <Col md="6" className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-users me-2 text-primary"></i>
                  {t("Patients")}
                </h6>
                <DetailRow label={t("Unique Patients Served")} value={stats.patients?.unique_patients || 0} />
              </CardBody>
            </Card>
          </Col>
          <Col md="6" className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-date me-2 text-success"></i>
                  {t("Appointment Summary")}
                </h6>
                <DetailRow label={t("Completed")} value={stats.appointments?.completed || 0} highlight="text-success" />
                <DetailRow label={t("Accepted / Active")} value={stats.appointments?.accepted || 0} />
                <DetailRow label={t("Cancelled")} value={stats.appointments?.cancelled || 0} highlight="text-danger" />
                <DetailRow label={t("Bid Submitted")} value={stats.appointments?.bid_submitted || 0} highlight="text-warning" />
                <DetailRow label={t("Payment Awaited")} value={stats.appointments?.payment_awaited || 0} highlight="text-warning" />
                <DetailRow label={t("Declined")} value={stats.appointments?.declined || 0} />
              </CardBody>
            </Card>
          </Col>
          <Col md="12" className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-note2 me-2 text-info"></i>
                  {t("Bids & Appointments Breakdown")}
                </h6>
                <Row>
                  <Col md="6">
                    <DetailRow label={t("Total Bids")} value={stats.appointments?.total_bids || 0} />
                    <DetailRow label={t("Pending Bids")} value={stats.appointments?.pending_bids || 0} highlight="text-warning" />
                    <DetailRow label={t("Accepted Bids")} value={stats.appointments?.accepted_bids || 0} highlight="text-success" />
                    <DetailRow label={t("Total Bid Value")} value={formatCurrency(stats.appointments?.total_bid_value)} />
                  </Col>
                  <Col md="6">
                    <DetailRow label={t("Total Appointment Records")} value={stats.appointments?.total_status_records || 0} />
                    <DetailRow label={t("Pending (Overall)")} value={stats.appointments?.pending || 0} highlight="text-warning" />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </StatsPanel>
    );
  }

  if (section === "financial") {
    return (
      <StatsPanel loading={false} stats={stats} t={t}>
        <Row>
          <Col md="6" className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-wallet me-2 text-success"></i>
                  {t("Ledger Summary")}
                </h6>
                <DetailRow label={t("Total Earned")} value={formatCurrency(stats.ledger?.total_amount)} highlight="text-success" />
                <DetailRow label={t("In Progress Amount")} value={formatCurrency(stats.ledger?.in_progress_amount)} highlight="text-warning" />
                <DetailRow label={t("Paid Amount")} value={formatCurrency(stats.ledger?.paid_amount)} />
                <DetailRow label={t("Withdrawal Amount (Ledger)")} value={formatCurrency(stats.ledger?.withdrawal_amount)} />
                <DetailRow label={t("Total Transactions")} value={stats.ledger?.total_transactions || 0} />
                <DetailRow label={t("Credit Transactions")} value={stats.ledger?.credit_count || 0} highlight="text-success" />
                <DetailRow label={t("Debit Transactions")} value={stats.ledger?.debit_count || 0} highlight="text-danger" />
                <DetailRow label={t("Pending Transactions")} value={stats.ledger?.pending_count || 0} highlight="text-warning" />
                <DetailRow label={t("In Progress Transactions")} value={stats.ledger?.in_progress_count || 0} />
                <DetailRow label={t("Paid Transactions")} value={stats.ledger?.paid_count || 0} />
              </CardBody>
            </Card>
          </Col>
          <Col md="6" className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-cash me-2 text-warning"></i>
                  {t("Withdrawals")}
                </h6>
                <DetailRow label={t("Total Withdrawn")} value={formatCurrency(stats.withdrawals?.total_withdrawn)} />
                <DetailRow label={t("Total Requests")} value={stats.withdrawals?.total || 0} />
                <DetailRow label={t("Pending")} value={stats.withdrawals?.pending || 0} highlight="text-warning" />
                <DetailRow label={t("Approved")} value={stats.withdrawals?.approved || 0} highlight="text-success" />
                <DetailRow label={t("Rejected")} value={stats.withdrawals?.rejected || 0} highlight="text-danger" />
                <DetailRow label={t("Earned (Transactions)")} value={formatCurrency(stats.ledger?.total_earned)} highlight="text-success" />
                <DetailRow label={t("Debited (Transactions)")} value={formatCurrency(stats.ledger?.total_debited)} highlight="text-danger" />
              </CardBody>
            </Card>
          </Col>
          <Col md="12" className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-bold mb-3">
                  <i className="pe-7s-clock me-2 text-primary"></i>
                  {t("Recent Transactions")}
                </h6>
                <RecentTransactionsList transactions={stats.recent_transactions} t={t} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </StatsPanel>
    );
  }

  return null;
};

export default DoctorStatsPanel;
